export function createApiClient(config) {
    const { baseUrl, getToken, getBusinessId, getAdminToken, onUnauthorized } = config;
    const fetchFn = config.fetchImpl ?? globalThis.fetch;
    async function request(path, { requireAuth = true, headers: extraHeaders, ...options } = {}) {
        const headers = {
            'Content-Type': 'application/json',
            ...extraHeaders,
        };
        if (requireAuth) {
            const token = await getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const businessId = getBusinessId?.();
            if (businessId) {
                headers['X-Business-ID'] = businessId;
            }
            const adminToken = getAdminToken?.();
            if (adminToken) {
                headers['X-Admin-Token'] = adminToken;
            }
        }
        const response = await fetchFn(`${baseUrl}${path}`, {
            ...options,
            headers,
        });
        const json = (await response.json());
        if (!response.ok) {
            if (requireAuth && response.status === 401) {
                onUnauthorized?.();
            }
            throw new Error(json.error?.message || 'Request failed');
        }
        return json.data;
    }
    // ===========================================================================
    // Public API
    // ===========================================================================
    const publicApi = {
        async getServices(slug) {
            return request(`/public/businesses/${slug}/services`, { requireAuth: false });
        },
        async getAvailability(slug, serviceId, date, providerId) {
            const params = new URLSearchParams({ serviceId, date });
            if (providerId)
                params.set('providerId', providerId);
            return request(`/public/businesses/${slug}/availability?${params}`, { requireAuth: false });
        },
        async createBookingIntent(slug, data) {
            return request(`/public/businesses/${slug}/booking-intent`, {
                requireAuth: false,
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async confirmBookingIntent(slug, intentId, sourceId) {
            return request(`/public/businesses/${slug}/booking-intent/${intentId}/confirm`, {
                requireAuth: false,
                method: 'POST',
                ...(sourceId ? { body: JSON.stringify({ sourceId }) } : {}),
            });
        },
        async getProviders(slug) {
            return request(`/public/businesses/${slug}/providers`, { requireAuth: false });
        },
        async getAddons(slug, serviceId) {
            return request(`/public/businesses/${slug}/addons?serviceId=${serviceId}`, {
                requireAuth: false,
            });
        },
        async rateProvider(slug, appointmentId, data) {
            return request(`/public/businesses/${slug}/appointments/${appointmentId}/rate`, {
                requireAuth: false,
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async lookupAppointment(slug, data) {
            return request(`/public/businesses/${slug}/appointments/lookup`, {
                requireAuth: false,
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async getAppointmentByToken(slug, token) {
            return request(`/public/businesses/${slug}/appointments/manage/${token}`, {
                requireAuth: false,
            });
        },
        async selfServiceCancel(slug, appointmentId, data) {
            return request(`/public/businesses/${slug}/appointments/${appointmentId}/cancel`, {
                requireAuth: false,
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async selfServiceReschedule(slug, appointmentId, data) {
            return request(`/public/businesses/${slug}/appointments/${appointmentId}/reschedule`, {
                requireAuth: false,
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
    };
    // ===========================================================================
    // Admin Business API
    // ===========================================================================
    const adminBusinessApi = {
        async getBusiness(businessId) {
            return request(`/admin/business/${businessId}`);
        },
        async create(data) {
            return request('/admin/business', {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async getBusinessHours(businessId) {
            return request(`/admin/business/${businessId}/business-hours`);
        },
        async getSubscription(businessId) {
            return request(`/admin/business/${businessId}/subscription`);
        },
        async createCheckoutSession(businessId, data) {
            return request(`/admin/business/${businessId}/billing/checkout`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async createBillingPortal(businessId, returnUrl) {
            return request(`/admin/business/${businessId}/billing/portal`, {
                method: 'POST',
                body: JSON.stringify({ returnUrl }),
            });
        },
        async updateBusinessHours(businessId, data) {
            return request(`/admin/business/${businessId}/business-hours`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async updateSettings(businessId, data) {
            return request(`/admin/business/${businessId}/settings`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async saveOnboardingDraft(businessId, draft) {
            return request(`/admin/business/${businessId}/onboarding-draft`, {
                method: 'POST',
                body: JSON.stringify(draft),
            });
        },
        async getOnboardingDraft(businessId) {
            return request(`/admin/business/${businessId}/onboarding-draft`);
        },
        async deleteOnboardingDraft(businessId) {
            return request(`/admin/business/${businessId}/onboarding-draft`, {
                method: 'DELETE',
            });
        },
        async getServices(businessId) {
            return request(`/admin/business/${businessId}/services`);
        },
        async createService(businessId, data) {
            return request(`/admin/business/${businessId}/services`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async updateService(businessId, serviceId, data) {
            return request(`/admin/business/${businessId}/services/${serviceId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async deleteService(businessId, serviceId) {
            return request(`/admin/business/${businessId}/services/${serviceId}`, {
                method: 'DELETE',
            });
        },
        async extractServicesFromPhoto(businessId, data) {
            return request(`/admin/business/${businessId}/services/extract-from-photo`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async getServiceAssignments(businessId) {
            return request(`/admin/business/${businessId}/service-assignments`);
        },
        async updateServiceProviders(businessId, serviceId, data) {
            return request(`/admin/business/${businessId}/services/${serviceId}/providers`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async getProviderServices(businessId, memberId) {
            return request(`/admin/business/${businessId}/providers/${memberId}/services`);
        },
        async getBundles(businessId) {
            return request(`/admin/business/${businessId}/bundles`);
        },
        async createBundle(businessId, data) {
            return request(`/admin/business/${businessId}/bundles`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async updateBundle(businessId, bundleId, data) {
            return request(`/admin/business/${businessId}/bundles/${bundleId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async deleteBundle(businessId, bundleId) {
            return request(`/admin/business/${businessId}/bundles/${bundleId}`, {
                method: 'DELETE',
            });
        },
        async getCancellationPolicy(businessId) {
            return request(`/admin/business/${businessId}/cancellation-policy`);
        },
        async updateCancellationPolicy(businessId, data) {
            return request(`/admin/business/${businessId}/cancellation-policy`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async getLoyaltyProgram(businessId) {
            return request(`/admin/business/${businessId}/loyalty-program`);
        },
        async updateLoyaltyProgram(businessId, data) {
            return request(`/admin/business/${businessId}/loyalty-program`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async applyBusinessTemplate(businessId, data) {
            return request(`/admin/business/${businessId}/apply-template`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
    };
    // ===========================================================================
    // Business Templates API
    // ===========================================================================
    const businessTemplatesApi = {
        async list(locale) {
            const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
            return request(`/admin/business-templates${query}`);
        },
        async get(templateId, locale) {
            const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
            return request(`/admin/business-templates/${templateId}${query}`);
        },
    };
    // ===========================================================================
    // Admin Appointments API
    // ===========================================================================
    const adminAppointmentsApi = {
        async getAppointments(businessId, startDate, endDate) {
            return request(`/admin/business/${businessId}/appointments?startDate=${startDate}&endDate=${endDate}`);
        },
        async createAppointment(businessId, data) {
            return request(`/admin/business/${businessId}/appointments`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async updateAppointment(businessId, appointmentId, data) {
            return request(`/admin/business/${businessId}/appointments/${appointmentId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async deleteAppointment(businessId, appointmentId) {
            return request(`/admin/business/${businessId}/appointments/${appointmentId}`, {
                method: 'DELETE',
            });
        },
        async requestConfirmation(businessId, appointmentId) {
            return request(`/admin/business/${businessId}/appointments/${appointmentId}/request-confirmation`, { method: 'POST' });
        },
    };
    // ===========================================================================
    // Admin Routing API
    // ===========================================================================
    const adminRoutingApi = {
        async getCandidates(businessId, params) {
            const query = new URLSearchParams({
                serviceId: params.serviceId,
                startTime: params.startTime,
                duration: String(params.duration),
            });
            if (params.clientId)
                query.set('clientId', params.clientId);
            return request(`/admin/business/${businessId}/route?${query}`);
        },
    };
    // ===========================================================================
    // Admin Clients API
    // ===========================================================================
    const adminClientsApi = {
        async getClients(businessId, search, limit = 50, offset = 0, tagIds, segments, sortBy, sortOrder, riskTier) {
            const params = new URLSearchParams({
                limit: String(limit),
                offset: String(offset),
            });
            if (search)
                params.set('search', search);
            if (tagIds && tagIds.length > 0)
                params.set('tagIds', tagIds.join(','));
            if (segments && segments.length > 0)
                params.set('segments', segments.join(','));
            if (sortBy)
                params.set('sortBy', sortBy);
            if (sortOrder)
                params.set('sortOrder', sortOrder);
            if (riskTier)
                params.set('riskTier', riskTier);
            return request(`/admin/business/${businessId}/clients?${params}`);
        },
        async getClientsAtRisk(businessId, limit = 10) {
            return adminClientsApi.getClients(businessId, undefined, limit, 0, undefined, undefined, 'churnRiskScore', 'desc', 'high');
        },
        async getClient(businessId, clientId) {
            return request(`/admin/business/${businessId}/clients/${clientId}`);
        },
        async getClientAppointmentHistory(businessId, clientId, limit = 50, offset = 0) {
            return request(`/admin/business/${businessId}/clients/${clientId}/appointments?limit=${limit}&offset=${offset}`);
        },
        async getClientTimeline(businessId, clientId, options = {}) {
            const params = new URLSearchParams();
            if (options.limit)
                params.set('limit', String(options.limit));
            if (options.cursor)
                params.set('cursor', options.cursor);
            if (options.type)
                params.set('type', options.type);
            if (options.source)
                params.set('source', options.source);
            if (options.fromDate)
                params.set('fromDate', String(options.fromDate));
            if (options.toDate)
                params.set('toDate', String(options.toDate));
            const query = params.toString();
            return request(`/admin/business/${businessId}/clients/${clientId}/timeline${query ? `?${query}` : ''}`);
        },
        async updateNotes(businessId, clientId, data) {
            return request(`/admin/business/${businessId}/clients/${clientId}/notes`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async updateTags(businessId, clientId, data) {
            return request(`/admin/business/${businessId}/clients/${clientId}/tags`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async updatePreferences(businessId, clientId, data) {
            return request(`/admin/business/${businessId}/clients/${clientId}/preferences`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async getLoyalty(businessId, clientId) {
            return request(`/admin/business/${businessId}/clients/${clientId}/loyalty`);
        },
        async getLoyaltyHistory(businessId, clientId, limit = 50, offset = 0) {
            return request(`/admin/business/${businessId}/clients/${clientId}/loyalty/history?limit=${limit}&offset=${offset}`);
        },
        async adjustLoyaltyBalance(businessId, clientId, data) {
            return request(`/admin/business/${businessId}/clients/${clientId}/loyalty/adjustments`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
    };
    // ===========================================================================
    // Admin Tags API
    // ===========================================================================
    const adminTagsApi = {
        async getTags(businessId) {
            return request(`/admin/business/${businessId}/tags`);
        },
        async createTag(businessId, data) {
            return request(`/admin/business/${businessId}/tags`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async updateTag(businessId, tagId, data) {
            return request(`/admin/business/${businessId}/tags/${tagId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async deleteTag(businessId, tagId) {
            return request(`/admin/business/${businessId}/tags/${tagId}`, {
                method: 'DELETE',
            });
        },
    };
    // ===========================================================================
    // Admin Providers API
    // ===========================================================================
    const adminProvidersApi = {
        async getProviders(businessId) {
            return request(`/admin/business/${businessId}/providers`);
        },
        async getMembers(businessId) {
            return request(`/admin/business/${businessId}/members`);
        },
        async createProvider(businessId, data) {
            return request(`/admin/business/${businessId}/providers`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async updateMembership(businessId, memberId, data) {
            return request(`/admin/business/${businessId}/providers/${memberId}`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        async deleteProvider(businessId, memberId) {
            return request(`/admin/business/${businessId}/providers/${memberId}`, {
                method: 'DELETE',
            });
        },
        async getProviderProfile(businessId, memberId) {
            return request(`/admin/business/${businessId}/providers/${memberId}/profile`);
        },
        async updateProviderProfile(businessId, memberId, data) {
            return request(`/admin/business/${businessId}/providers/${memberId}/profile`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async addSelfAsProvider(businessId, data) {
            return request(`/admin/business/${businessId}/providers/self`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async getCapacity(businessId, memberId) {
            return request(`/admin/business/${businessId}/providers/${memberId}/capacity`);
        },
        async updateCapacity(businessId, memberId, data) {
            return request(`/admin/business/${businessId}/providers/${memberId}/capacity`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async getSkills(businessId, memberId) {
            return request(`/admin/business/${businessId}/providers/${memberId}/skills`);
        },
        async updateSkills(businessId, memberId, data) {
            return request(`/admin/business/${businessId}/providers/${memberId}/skills`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async getProviderPerformance(businessId, providerId, params) {
            const qs = new URLSearchParams();
            if (params?.from)
                qs.set('from', params.from);
            if (params?.to)
                qs.set('to', params.to);
            const query = qs.toString();
            return request(`/admin/business/${businessId}/providers/${providerId}/performance${query ? `?${query}` : ''}`);
        },
    };
    // ===========================================================================
    // Admin Invites API
    // ===========================================================================
    const adminInvitesApi = {
        async createInvite(businessId, data) {
            return request(`/admin/business/${businessId}/invites`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async listInvites(businessId) {
            return request(`/admin/business/${businessId}/invites`);
        },
        async revokeInvite(businessId, inviteId) {
            return request(`/admin/business/${businessId}/invites/${inviteId}`, {
                method: 'DELETE',
            });
        },
    };
    // ===========================================================================
    // Provider Self-Service API
    // ===========================================================================
    const providerApi = {
        async getProfile() {
            return request('/provider/profile');
        },
        async updateProfile(data) {
            return request('/provider/profile', {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async getMyAppointments(startDate, endDate) {
            const params = new URLSearchParams();
            if (startDate)
                params.set('startDate', startDate);
            if (endDate)
                params.set('endDate', endDate);
            const qs = params.toString();
            return request(`/provider/appointments${qs ? `?${qs}` : ''}`);
        },
        async getMyAppointmentsToday() {
            return request('/provider/appointments/today');
        },
        async getMyStats(businessId) {
            const qs = businessId ? `?businessId=${encodeURIComponent(businessId)}` : '';
            return request(`/provider/stats${qs}`);
        },
        async getMyPerformance(params) {
            const qs = new URLSearchParams();
            if (params?.businessId)
                qs.set('businessId', params.businessId);
            if (params?.from)
                qs.set('from', params.from);
            if (params?.to)
                qs.set('to', params.to);
            const query = qs.toString();
            return request(`/provider/performance${query ? `?${query}` : ''}`);
        },
        async getMySkills() {
            return request('/provider/skills');
        },
        async updateMySkills(data) {
            return request('/provider/skills', {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
    };
    // ===========================================================================
    // Public Invites API
    // ===========================================================================
    const publicInvitesApi = {
        async getInvite(token) {
            return request(`/invites/${encodeURIComponent(token)}`);
        },
        async acceptInvite(token) {
            return request(`/invites/${encodeURIComponent(token)}/accept`, {
                method: 'POST',
            });
        },
    };
    // ===========================================================================
    // Admin Reminders API
    // ===========================================================================
    const adminRemindersApi = {
        async getPolicy(businessId) {
            return request(`/admin/business/${businessId}/reminder-policy`);
        },
        async updatePolicy(businessId, data) {
            return request(`/admin/business/${businessId}/reminder-policy`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
    };
    // ===========================================================================
    // Admin Follow-Up Policy API
    // ===========================================================================
    const adminFollowUpApi = {
        async getPolicy(businessId) {
            return request(`/admin/business/${businessId}/follow-up-policy`);
        },
        async updatePolicy(businessId, data) {
            return request(`/admin/business/${businessId}/follow-up-policy`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async triggerScan() {
            return request(`/admin/follow-ups/scan`, { method: 'POST' });
        },
    };
    // ===========================================================================
    // Admin Alerts API
    // ===========================================================================
    const adminAlertsApi = {
        async getAlerts(businessId) {
            return request(`/admin/business/${businessId}/alerts`);
        },
        async dismissAlert(businessId, alertId) {
            return request(`/admin/business/${businessId}/alerts/${alertId}/dismiss`, {
                method: 'POST',
            });
        },
        async snoozeAlert(businessId, alertId, data) {
            return request(`/admin/business/${businessId}/alerts/${alertId}`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        async triggerScan(businessId) {
            return request(`/admin/business/${businessId}/alerts/scan`, {
                method: 'POST',
            });
        },
    };
    // ===========================================================================
    // Admin Risk Scoring Policy API
    // ===========================================================================
    const adminRiskPolicyApi = {
        async getPolicy(businessId) {
            return request(`/admin/business/${businessId}/risk-policy`);
        },
        async updatePolicy(businessId, data) {
            return request(`/admin/business/${businessId}/risk-policy`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async getPreview(businessId, threshold) {
            return request(`/admin/business/${businessId}/risk-policy/preview?threshold=${threshold}`);
        },
    };
    // ===========================================================================
    // Scheduler API
    // ===========================================================================
    const schedulerApi = {
        async createProviderSchedule(providerId, businessId, businessHours) {
            return request(`/admin/scheduler/providers/${providerId}/schedule`, {
                method: 'POST',
                body: JSON.stringify({ business_id: businessId, business_hours: businessHours }),
            });
        },
        async updateProviderSchedule(providerId, businessId, businessHours) {
            return request(`/admin/scheduler/providers/${providerId}/schedule`, {
                method: 'PUT',
                body: JSON.stringify({ business_id: businessId, business_hours: businessHours }),
            });
        },
        async getProviderSchedule(providerId) {
            return request(`/admin/scheduler/providers/${providerId}/schedule`);
        },
        async updateProviderScheduleV2(providerId, data) {
            return request(`/admin/scheduler/providers/${providerId}/schedule`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async patchScheduleException(providerId, action, exception) {
            return request(`/admin/scheduler/providers/${providerId}/schedule/exceptions`, {
                method: 'PATCH',
                body: JSON.stringify({ action, exception }),
            });
        },
        async getScheduleAvailability(providerId, startDate, endDate) {
            return request(`/admin/scheduler/providers/${providerId}/schedule/availability?startDate=${startDate}&endDate=${endDate}`);
        },
    };
    // ===========================================================================
    // Admin Payments API
    // ===========================================================================
    const adminPaymentsApi = {
        async getConnectStatus(businessId) {
            return request(`/admin/business/${businessId}/payments/connect/status`);
        },
        async startOnboarding(businessId, options) {
            return request(`/admin/business/${businessId}/payments/connect/onboard`, {
                method: 'POST',
                ...(options ? { body: JSON.stringify(options) } : {}),
            });
        },
        async refreshOnboarding(businessId, options) {
            return request(`/admin/business/${businessId}/payments/connect/refresh`, {
                method: 'POST',
                ...(options ? { body: JSON.stringify(options) } : {}),
            });
        },
        async getSettings(businessId) {
            return request(`/admin/business/${businessId}/payments/settings`);
        },
        async updateSettings(businessId, data) {
            return request(`/admin/business/${businessId}/payments/settings`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
        },
        async getTransactions(businessId, limit = 50, offset = 0) {
            return request(`/admin/business/${businessId}/payments/transactions?limit=${limit}&offset=${offset}`);
        },
        async getEvents(businessId, limit = 50, offset = 0) {
            return request(`/admin/business/${businessId}/payments/events?limit=${limit}&offset=${offset}`);
        },
        async refundPayment(businessId, paymentId) {
            return request(`/admin/business/${businessId}/payments/${paymentId}/refund`, {
                method: 'POST',
            });
        },
        async authorizeSquare(businessId, returnUrl) {
            return request(`/admin/business/${businessId}/payments/square/authorize`, {
                method: 'POST',
                body: JSON.stringify({ returnUrl }),
            });
        },
        async disconnectSquare(businessId) {
            return request(`/admin/business/${businessId}/payments/square/disconnect`, {
                method: 'POST',
            });
        },
    };
    // ===========================================================================
    // Admin Messages API
    // ===========================================================================
    const adminMessagesApi = {
        async getConversations(businessId) {
            return request(`/admin/business/${businessId}/conversations`);
        },
        async getMessages(businessId, clientId, limit = 50, offset = 0) {
            return request(`/admin/business/${businessId}/clients/${clientId}/messages?limit=${limit}&offset=${offset}`);
        },
        async sendMessage(businessId, clientId, data) {
            return request(`/admin/business/${businessId}/clients/${clientId}/messages`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async markRead(businessId, clientId) {
            return request(`/admin/business/${businessId}/clients/${clientId}/messages/read`, {
                method: 'PATCH',
            });
        },
        async getUnreadCount(businessId) {
            return request(`/admin/business/${businessId}/messages/unread-count`);
        },
        async getUnmatched(businessId, limit = 50, offset = 0) {
            return request(`/admin/business/${businessId}/messages/unmatched?limit=${limit}&offset=${offset}`);
        },
        async searchMessages(businessId, q, limit = 50, offset = 0) {
            const params = new URLSearchParams({ q, limit: String(limit), offset: String(offset) });
            return request(`/admin/business/${businessId}/messages/search?${params}`);
        },
    };
    // ===========================================================================
    // Admin Suggestions API
    // ===========================================================================
    const adminSuggestionsApi = {
        async getSuggestions(businessId, status, limit = 50, offset = 0) {
            const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
            if (status)
                params.set('status', status);
            return request(`/admin/business/${businessId}/suggestions?${params}`);
        },
        async getSuggestion(businessId, suggestionId) {
            return request(`/admin/business/${businessId}/suggestions/${suggestionId}`);
        },
        async createSuggestion(businessId, data) {
            return request(`/admin/business/${businessId}/suggestions`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async reviewSuggestion(businessId, suggestionId, data) {
            return request(`/admin/business/${businessId}/suggestions/${suggestionId}`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        async bulkReview(businessId, data) {
            return request(`/admin/business/${businessId}/suggestions/bulk-review`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
    };
    // ===========================================================================
    // Admin Reports API
    // ===========================================================================
    const adminReportsApi = {
        async getNoShowReport(businessId, filters = {}) {
            const params = new URLSearchParams();
            if (filters.from)
                params.set('from', filters.from);
            if (filters.to)
                params.set('to', filters.to);
            if (filters.providerId)
                params.set('providerId', filters.providerId);
            if (filters.serviceId)
                params.set('serviceId', filters.serviceId);
            if (filters.granularity)
                params.set('granularity', filters.granularity);
            const qs = params.toString();
            const data = await request(`/admin/business/${businessId}/reports/no-show${qs ? `?${qs}` : ''}`);
            return (data.data ?? data);
        },
        async getRetentionReport(businessId, filters = {}) {
            const params = new URLSearchParams();
            if (filters.from)
                params.set('from', filters.from);
            if (filters.to)
                params.set('to', filters.to);
            if (filters.lapsedThresholdDays != null)
                params.set('lapsedThresholdDays', String(filters.lapsedThresholdDays));
            const qs = params.toString();
            const data = await request(`/admin/business/${businessId}/reports/retention${qs ? `?${qs}` : ''}`);
            return (data.data ?? data);
        },
    };
    // ===========================================================================
    // Admin Events API (business activity log)
    // ===========================================================================
    const adminEventsApi = {
        async getEvents(businessId, options = {}) {
            const params = new URLSearchParams();
            if (options.limit != null)
                params.set('limit', String(options.limit));
            if (options.cursor)
                params.set('cursor', options.cursor);
            if (options.types && options.types.length > 0)
                params.set('types', options.types.join(','));
            if (options.source)
                params.set('source', options.source);
            if (options.clientId)
                params.set('clientId', options.clientId);
            if (options.startTime != null)
                params.set('startTime', String(options.startTime));
            if (options.endTime != null)
                params.set('endTime', String(options.endTime));
            const qs = params.toString();
            const data = await request(`/admin/business/${businessId}/events${qs ? `?${qs}` : ''}`);
            return (data.data ?? data);
        },
    };
    // ===========================================================================
    // Devices API
    // ===========================================================================
    const devices = {
        async register(token, platform, businessId) {
            const body = {
                token,
                platform,
                ...(businessId ? { businessId } : {}),
            };
            return request('/v1/devices', {
                method: 'POST',
                body: JSON.stringify(body),
            });
        },
        async delete(id) {
            return request(`/v1/devices/${id}`, { method: 'DELETE' });
        },
    };
    const auth = {
        async getMe() {
            return request('/auth/me', { method: 'GET' });
        },
        async updateMe(body) {
            return request('/auth/me', {
                method: 'PATCH',
                body: JSON.stringify(body),
            });
        },
        /**
         * Sync the authenticated user into the backend database.
         * Must be called when getMe() returns 404 (user exists in Auth0 but not in
         * our DB — e.g. first login, or after a DB reset in local dev).
         * Pass email/name from the ID token so the worker doesn't need to call
         * /userinfo (which rejects PKCE/custom-audience tokens on mobile).
         */
        async sync(profile) {
            await request('/auth/sync', {
                method: 'POST',
                body: JSON.stringify(profile ?? {}),
            });
        },
    };
    // ===========================================================================
    // Session API (mobile→web bridge)
    // ===========================================================================
    const session = {
        async createBridge(profile) {
            return request('/v1/session/bridge', {
                method: 'POST',
                body: JSON.stringify(profile ?? {}),
            });
        },
    };
    // ===========================================================================
    // Admin Analytics API (platform-level visitor analytics)
    // ===========================================================================
    const adminAnalyticsApi = {
        async getSummary(filters) {
            const params = new URLSearchParams();
            params.set('from', filters.from);
            params.set('to', filters.to);
            if (filters.page)
                params.set('page', filters.page);
            if (filters.experiment_id)
                params.set('experiment_id', filters.experiment_id);
            const qs = params.toString();
            const data = await request(`/admin/analytics/summary${qs ? `?${qs}` : ''}`);
            return (data.data ?? data);
        },
        async getEvents(filters) {
            const params = new URLSearchParams();
            params.set('from', filters.from);
            params.set('to', filters.to);
            if (filters.event_name)
                params.set('event_name', filters.event_name);
            if (filters.limit)
                params.set('limit', String(filters.limit));
            if (filters.cursor)
                params.set('cursor', filters.cursor);
            const qs = params.toString();
            const data = await request(`/admin/analytics/events${qs ? `?${qs}` : ''}`);
            return (data.data ?? data);
        },
        async exportEventsAsCSV(filters) {
            const params = new URLSearchParams();
            params.set('from', filters.from);
            params.set('to', filters.to);
            if (filters.event_name)
                params.set('event_name', filters.event_name);
            const qs = params.toString();
            const headers = {};
            const adminToken = getAdminToken?.();
            if (adminToken) {
                headers['X-Admin-Token'] = adminToken;
            }
            const response = await fetchFn(`${baseUrl}/admin/analytics/events/export${qs ? `?${qs}` : ''}`, {
                headers,
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.blob();
        },
        async getExperimentFunnel(experimentId) {
            const data = await request(`/admin/analytics/experiments/${experimentId}`);
            return (data.data ?? data);
        },
    };
    const adminOutreachApi = {
        async createAudience(body) {
            return request('/admin/user-outreach/audiences', {
                method: 'POST',
                body: JSON.stringify(body),
            });
        },
        async getAudiencePage(audienceId, page) {
            return request(`/admin/user-outreach/audiences/${encodeURIComponent(audienceId)}?page=${page}`);
        },
        async previewEmail(audienceId, body) {
            return request(`/admin/user-outreach/audiences/${encodeURIComponent(audienceId)}/email-preview`, { method: 'POST', body: JSON.stringify(body) });
        },
        async sendTestEmail(audienceId, body) {
            return request(`/admin/user-outreach/audiences/${encodeURIComponent(audienceId)}/test-email`, { method: 'POST', body: JSON.stringify(body) });
        },
        async createCampaign(audienceId, body) {
            return request(`/admin/user-outreach/audiences/${encodeURIComponent(audienceId)}/campaigns`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
        },
        async getCampaign(campaignId) {
            return request(`/admin/user-outreach/campaigns/${encodeURIComponent(campaignId)}`);
        },
    };
    // ===========================================================================
    // Platform Admin API (Tier-A: cross-tenant platform operations)
    // ===========================================================================
    const platformAdminApi = {
        // Businesses
        async listBusinesses() {
            const data = await request('/admin/businesses');
            return data || [];
        },
        async getBusiness(businessId) {
            return request(`/admin/business/${businessId}`);
        },
        async createBusiness(data) {
            return request('/admin/business', {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        async deleteBusiness(businessId) {
            return request(`/admin/business/${businessId}`, {
                method: 'DELETE',
            });
        },
        async updateSubscriptionTier(businessId, tier) {
            return request(`/admin/business/${businessId}/subscription`, {
                method: 'PATCH',
                body: JSON.stringify({ tier }),
            });
        },
        // Maintenance
        async cleanupOrphanMemberships(dryRun = true) {
            return request('/admin/maintenance/cleanup-orphan-memberships', {
                method: 'POST',
                body: JSON.stringify({ dryRun }),
            });
        },
        // Users
        async listUsers() {
            const data = await request('/admin/users');
            return data || [];
        },
        async deleteUser(userId) {
            return request(`/admin/users/${userId}`, {
                method: 'DELETE',
            });
        },
        // Providers (cross-business)
        async listAllProviders() {
            const data = await request('/admin/providers');
            return data || [];
        },
        // Push Test
        async testPush(businessId, payload) {
            return request(`/admin/internal/business/${businessId}/push/test`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
        },
        // Admin Auth Verification
        async verifyAdminEmail(auth0Token, email) {
            return request('/admin/auth/verify-email', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${auth0Token}`,
                },
                body: JSON.stringify({ email }),
            });
        },
        async verifyAdminToken() {
            return request('/admin/auth/verify-token', {
                method: 'GET',
            });
        },
    };
    // ─── Nudge Delivery API ───────────────────────────────────────────────────
    const nudgeDeliveryApi = {
        async getAll() {
            return request('/admin/nudges/delivery');
        },
        async get(nudgeId) {
            return request(`/admin/nudges/delivery/${encodeURIComponent(nudgeId)}`);
        },
        async record(nudgeId) {
            return request('/admin/nudges/delivery/record', {
                method: 'POST',
                body: JSON.stringify({ nudgeId }),
            });
        },
    };
    return {
        auth,
        publicApi,
        adminBusinessApi,
        businessTemplatesApi,
        adminAppointmentsApi,
        adminRoutingApi,
        adminClientsApi,
        adminTagsApi,
        adminProvidersApi,
        adminInvitesApi,
        providerApi,
        publicInvitesApi,
        adminRemindersApi,
        adminFollowUpApi,
        adminAlertsApi,
        adminRiskPolicyApi,
        schedulerApi,
        adminPaymentsApi,
        adminMessagesApi,
        adminSuggestionsApi,
        adminReportsApi,
        adminEventsApi,
        devices,
        session,
        adminAnalyticsApi,
        adminOutreachApi,
        platformAdminApi,
        nudgeDeliveryApi,
    };
}
//# sourceMappingURL=client.js.map